// src/components/SliceMachine/NeumoContact/NeumoContact.tsx
'use client';

import React, { Fragment, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import classes from './NeumoContact.module.scss';
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
} from 'lucide-react';
import { ObfuscatedEmail } from '@/src/components/ObfuscatedEmail/ObfuscatedEmail';
import { gaClick, gaCta, gaExternalLink, gaFormSubmit } from '@/src/helpers/gtag';

type NeumoContactItem = {
  type: 'phone' | 'whatsapp' | 'email' | 'location';
  label: string;
  value: string;
  href?: string;
};

export type NeumoContactSlice = {
  type: 'neumo-contact';
  badge?: string;
  catchphrase?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  contacts?: NeumoContactItem[];
  note?: string;

  formTitle?: string;
  formSubtitle?: string;
  formSubmitLabel?: string;
  formAction?: string;

  formNameLabel?: string;
  formNamePlaceholder?: string;
  formEmailLabel?: string;
  formEmailPlaceholder?: string;
  formTopicLabel?: string;
  formTopicPlaceholder?: string;
  formMessageLabel?: string;
  formMessagePlaceholder?: string;
  formHelperText?: string;
  formSuccessMessage?: string;
  formErrorMessage?: string;
};

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function NeumoContact({ data, lang }: { data: NeumoContactSlice; lang: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isSent = searchParams?.get('sent') === '1';
  const hasError = searchParams?.get('error');
  const topic = searchParams?.get('topic') ?? '';

  const isEn = lang === 'en';

  // Anti-spam time trap: stamped when the form mounts on the client. Bots that
  // fill and submit programmatically right away post with a near-zero elapsed time.
  const startedAtRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (startedAtRef.current) {
      startedAtRef.current.value = String(Date.now());
    }
  }, []);

  useEffect(() => {
    if (['rdv', 'appointment', 'rendez-vous', 'nda'].includes(topic)) {
      router.replace(`/${lang}/rendez-vous`);
    }
  }, [topic, lang, router]);

  const {
    badge,
    catchphrase,
    title,
    highlightedTitle,
    description,
    contacts = [],
    note,

    formTitle,
    formSubtitle,
    formSubmitLabel,
    formAction = '/api/contact',

    formNameLabel,
    formNamePlaceholder,
    formEmailLabel,
    formEmailPlaceholder,
    formTopicLabel,
    formTopicPlaceholder,
    formMessageLabel,
    formMessagePlaceholder,
    formHelperText,
    formSuccessMessage,
    formErrorMessage,
  } = data;

  const resolvedTitle = title ?? (isEn ? 'Ready to start?' : 'Prêt à démarrer ?');
  const resolvedHighlightedTitle =
    highlightedTitle ?? (isEn ? '*We handle the rest.*' : "*On s'occupe de tout.*");
  const resolvedDescription =
    description ??
    (isEn
      ? 'Tell me what you are building. I usually reply within one business day.'
      : 'Dites-moi ce que vous construisez. Je réponds en général sous un jour ouvré.');

  const resolvedFormTitle = formTitle ?? (isEn ? 'Send a message' : 'Envoyer un message');
  const resolvedFormSubtitle = formSubtitle ?? '';
  const resolvedFormSubmitLabel = formSubmitLabel ?? (isEn ? 'Send message' : 'Envoyer');

  const resolvedNameLabel = formNameLabel ?? (isEn ? 'Name' : 'Nom');
  const resolvedNamePlaceholder = formNamePlaceholder ?? (isEn ? 'Your name' : 'Votre nom');
  const resolvedEmailLabel = formEmailLabel ?? 'Email';
  const resolvedEmailPlaceholder = formEmailPlaceholder ?? 'contact@averiane.com';
  const resolvedTopicLabel = formTopicLabel ?? (isEn ? 'Project / topic' : 'Projet / sujet');
  const resolvedTopicPlaceholder =
    formTopicPlaceholder ?? (isEn ? 'MVP, MVP, data, software…' : 'MVP, MVP, data, logiciel…');
  const resolvedMessageLabel = formMessageLabel ?? (isEn ? 'Message' : 'Message');
  const resolvedMessagePlaceholder =
    formMessagePlaceholder ??
    (isEn
      ? 'Tell us about your project, timelines and tools…'
      : 'Parlez-nous de votre projet, délais et outils…');
  const resolvedHelperText =
    formHelperText ??
    note ??
    (isEn ? 'We usually reply within 24h on business days.' : 'Réponse sous 24h en jours ouvrés.');
  const resolvedSuccessMessage =
    formSuccessMessage ??
    (isEn
      ? 'Thank you, your message has been sent. We’ll get back to you shortly.'
      : 'Merci, votre message a bien été envoyé. Nous vous recontactons rapidement.');
  const resolvedErrorMessage =
    formErrorMessage ??
    (isEn
      ? 'Something went wrong. Please try again or email us directly.'
      : 'Une erreur est survenue. Réessayez ou envoyez-nous un email directement.');

  const phoneContact = contacts.find((c) => c.type === 'phone');
  const whatsappContact = contacts.find((c) => c.type === 'whatsapp');
  const emailContact = contacts.find((c) => c.type === 'email');
  const locationContact = contacts.find((c) => c.type === 'location');

  return (
    <section className={classes.contactSection} aria-labelledby="contact-neumo-title">
      <div className={classes.introBanner}>
        <div className={classes.bannerContent}>
          {catchphrase && <p className={classes.bannerCatchphrase}>{catchphrase}</p>}

          <h1 id="contact-neumo-title" className={classes.bannerTitle}>
            {renderTitle(resolvedTitle)} <em>{renderTitle(resolvedHighlightedTitle)}</em>
          </h1>

          <p className={classes.bannerDescription}>{resolvedDescription}</p>

          <div className={classes.bannerActions}>
            {phoneContact && (
              <a
                href={phoneContact.href}
                className={classes.bannerCall}
                onClick={() => gaClick('contact_page_phone', { href: phoneContact.href })}
              >
                <Phone size={18} />
                {isEn ? 'Call me directly' : "M'appeler directement"}
              </a>
            )}
            <Link
              href={`/${lang}/rendez-vous`}
              className={classes.bannerBooking}
              onClick={() =>
                gaCta(
                  isEn ? 'Book a call' : 'Réserver un appel',
                  'contact_page_booking',
                  `/${lang}/rendez-vous`
                )
              }
            >
              <Calendar size={18} />
              {isEn ? 'Book a call' : 'Réserver un appel'}
            </Link>
          </div>
        </div>
      </div>

      <div className={classes.container}>
        <div className={classes.left}>
          <div className={classes.contactPanel}>
            <span className={classes.panelLabel}>{isEn ? 'Get in touch' : 'Contact rapide'}</span>

            {phoneContact && (
              <a
                href={phoneContact.href}
                className={classes.panelItem}
                onClick={() => gaClick('contact_panel_phone', { href: phoneContact.href })}
              >
                <Phone size={16} />
                <span>{phoneContact.value}</span>
              </a>
            )}

            {whatsappContact && (
              <a
                href={whatsappContact.href || '#'}
                className={classes.panelItem}
                onClick={() => gaExternalLink(whatsappContact.href || '', 'WhatsApp')}
              >
                <MessageCircle size={16} />
                <span>WhatsApp</span>
              </a>
            )}

            {emailContact && (
              <ObfuscatedEmail
                email={emailContact.value}
                asLink
                linkClassName={classes.panelItem}
                icon={<Mail size={16} />}
                onClick={() =>
                  gaClick('contact_panel_email', { href: `mailto:${emailContact.value}` })
                }
              />
            )}

            {locationContact && (
              <div className={classes.panelItem}>
                <MapPin size={16} />
                <span>{locationContact.value}</span>
              </div>
            )}
          </div>
        </div>

        <div className={classes.right}>
          <div className={classes.formCard}>
            <div className={classes.formHeader}>
              {isSent && (
                <div
                  className={`${classes.statusBanner} ${classes.statusSuccess}`}
                  role="status"
                  aria-live="polite"
                >
                  <CheckCircle2 size={18} aria-hidden="true" />
                  <span>{resolvedSuccessMessage}</span>
                </div>
              )}

              {hasError && (
                <div
                  className={`${classes.statusBanner} ${classes.statusError}`}
                  role="alert"
                  aria-live="assertive"
                >
                  <AlertCircle size={18} aria-hidden="true" />
                  <span>{resolvedErrorMessage}</span>
                </div>
              )}
              {badge && (
                <div className={classes.formBadge}>
                  <span>{badge}</span>
                </div>
              )}

              <h2 className={classes.formTitle}>{resolvedFormTitle}</h2>

              {resolvedFormSubtitle && (
                <p className={classes.formSubtitle}>{resolvedFormSubtitle}</p>
              )}
            </div>

            <form
              id="contact-form"
              data-form-type="contact"
              action={formAction}
              method="post"
              className={classes.formBody}
              onSubmit={() => gaFormSubmit('contact')}
            >
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                style={{
                  position: 'absolute',
                  opacity: 0,
                  pointerEvents: 'none',
                  height: 0,
                  width: 0,
                  border: 'none',
                  padding: 0,
                  margin: 0,
                }}
                aria-hidden="true"
              />
              <input
                type="hidden"
                name="form_started"
                ref={startedAtRef}
                defaultValue=""
              />
              <div className={classes.formGrid}>
                <div className={classes.field}>
                  <label htmlFor="contact-name" className={classes.label}>
                    {resolvedNameLabel}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className={classes.input}
                    placeholder={resolvedNamePlaceholder}
                  />
                </div>

                <div className={classes.field}>
                  <label htmlFor="contact-email" className={classes.label}>
                    {resolvedEmailLabel}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className={classes.input}
                    placeholder={resolvedEmailPlaceholder}
                  />
                </div>

                <div className={classes.field}>
                  <label htmlFor="contact-topic" className={classes.label}>
                    {resolvedTopicLabel}
                  </label>
                  <input
                    id="contact-topic"
                    name="topic"
                    type="text"
                    className={classes.input}
                    placeholder={resolvedTopicPlaceholder}
                    defaultValue={topic === 'message' ? '' : topic}
                  />
                </div>
              </div>

              <div className={classes.field}>
                <label htmlFor="contact-message" className={classes.label}>
                  {resolvedMessageLabel}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  className={classes.textarea}
                  rows={4}
                  placeholder={resolvedMessagePlaceholder}
                />
              </div>

              <div className={classes.submitRow}>
                <button type="submit" className={classes.submitButton}>
                  {resolvedFormSubmitLabel}
                  <ArrowRight size={16} aria-hidden="true" />
                </button>

                <p className={classes.helperText}>{resolvedHelperText}</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
